import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { passwordSchema } from "@wodiary/shared";
import { api, ApiError } from "../api/client.js";
import { errorMessage } from "../lib/errors.js";
import { NavBar } from "../components/NavBar.js";
import { useToast } from "../components/ToastContext.js";

/** 修改密码。需要当前密码 —— 理由见 `app.ts` 的实施期裁定。 */
export function PasswordPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = passwordSchema.safeParse(next);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "新密码至少 8 位。");
      return;
    }
    if (next !== confirm) {
      setError("两次输入的新密码不一样。");
      return;
    }
    setBusy(true);
    try {
      await api.changePassword(current, next);
      toast.show("密码已修改");
      navigate("/settings", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "invalid_current_password") {
        setError("当前密码不对。");
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <NavBar title="修改密码" back />
      <form className="page" onSubmit={onSubmit}>
        {error !== null && <div className="banner banner-error">{error}</div>}

        <label className="field-block">
          <span className="field-label">当前密码</span>
          <span className="field">
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              autoFocus
            />
          </span>
        </label>

        <label className="field-block">
          <span className="field-label">新密码</span>
          <span className="field">
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="至少 8 位"
              autoComplete="new-password"
            />
          </span>
          <span className="field-hint">
            不强制大小写或符号 —— 足够长比足够复杂更有效。
          </span>
        </label>

        <label className="field-block">
          <span className="field-label">再输一次</span>
          <span className="field">
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </span>
        </label>

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={busy || current === "" || next === "" || confirm === ""}
        >
          {busy ? "修改中…" : "修改密码"}
        </button>
        <p className="foot-note">
          忘了当前密码？那走服务器命令行 `pnpm ops reset-password`，应用内没有找回入口。
        </p>
      </form>
    </>
  );
}
